import React, { useEffect, useState } from 'react';
import BaseModal from '../Modals/BaseModal';
import { captureLandingLead } from '../../services/leadCaptureService';
import { captureAnalyticsEvent, FUNNEL_EVENTS } from '../../utils/analytics';
import './ContactLeadModal.css';

const VARIANT_CONFIG = {
  demo: {
    title: 'Pide una demo',
    subtitle: 'Déjanos tus datos y te contactaremos para mostrarte Trimergy con un caso de tu sector.',
    submitLabel: 'Solicitar demo',
    successTitle: '¡Solicitud enviada!',
    successMessage: 'Gracias. Nos pondremos en contacto contigo en breve para coordinar la demo.',
    analyticsOpen: FUNNEL_EVENTS.DEMO_MODAL_OPENED,
    analyticsSubmit: FUNNEL_EVENTS.DEMO_LEAD_CAPTURED,
    showPhone: true,
  },
  optrim_waitlist: {
    title: 'Optrim — lista de espera',
    subtitle:
      'Sé el primero en conocer el lanzamiento de Optrim, la nueva solución de Trimergy para la gestión activa de sistemas BESS.',
    submitLabel: 'Quiero enterarme',
    successTitle: '¡Estás en la lista!',
    successMessage: 'Te avisaremos en cuanto Optrim esté disponible.',
    analyticsOpen: 'optrim_waitlist_opened',
    analyticsSubmit: FUNNEL_EVENTS.OPTRIM_WAITLIST_CAPTURED,
    showPhone: false,
  },
};

export default function ContactLeadModal({ isOpen, variant = 'demo', source, page, onClose }) {
  const config = VARIANT_CONFIG[variant] || VARIANT_CONFIG.demo;
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    setEmail('');
    setName('');
    setCompany('');
    setPhone('');
    setError('');
    setIsSubmitting(false);
    setIsSuccess(false);

    captureAnalyticsEvent(config.analyticsOpen, { source, page, variant });
  }, [isOpen, source, page, variant, config.analyticsOpen]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      await captureLandingLead({
        variant,
        email,
        name,
        company,
        phone,
        source,
        page,
      });

      captureAnalyticsEvent(config.analyticsSubmit, {
        source,
        page,
        variant,
        has_name: Boolean(name?.trim()),
        has_company: Boolean(company?.trim()),
        has_phone: Boolean(phone?.trim()),
      });

      setIsSuccess(true);
    } catch (submitError) {
      setError(submitError.message || 'No se pudo enviar la solicitud. Inténtalo de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const footer = isSuccess ? (
    <button type="button" className="contact-lead-modal__submit" onClick={onClose}>
      Cerrar
    </button>
  ) : (
    <>
      <button type="button" className="contact-lead-modal__cancel" onClick={onClose} disabled={isSubmitting}>
        Cancelar
      </button>
      <button
        type="submit"
        form="contact-lead-modal-form"
        className="contact-lead-modal__submit"
        disabled={isSubmitting}
      >
        {isSubmitting ? 'Enviando...' : config.submitLabel}
      </button>
    </>
  );

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} title={config.title} size="small" footer={footer} className="contact-lead-modal">
      {isSuccess ? (
        <div className="contact-lead-modal__success">
          <h3>{config.successTitle}</h3>
          <p>{config.successMessage}</p>
        </div>
      ) : (
        <>
          <p className="contact-lead-modal__subtitle">{config.subtitle}</p>
          <form id="contact-lead-modal-form" className="contact-lead-modal__form" onSubmit={handleSubmit}>
            <div className="contact-lead-modal__field">
              <label htmlFor="contact-lead-email">Email</label>
              <input
                id="contact-lead-email"
                type="email"
                placeholder="tu@empresa.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isSubmitting}
                autoComplete="email"
              />
            </div>

            <div className="contact-lead-modal__field">
              <label htmlFor="contact-lead-name">Nombre</label>
              <input
                id="contact-lead-name"
                type="text"
                placeholder="Tu nombre"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isSubmitting}
                autoComplete="name"
              />
            </div>

            <div className="contact-lead-modal__field">
              <label htmlFor="contact-lead-company">Empresa</label>
              <input
                id="contact-lead-company"
                type="text"
                placeholder="Nombre de tu empresa"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                disabled={isSubmitting}
                autoComplete="organization"
              />
            </div>

            {config.showPhone ? (
              <div className="contact-lead-modal__field">
                <label htmlFor="contact-lead-phone">Teléfono (opcional)</label>
                <input
                  id="contact-lead-phone"
                  type="tel"
                  placeholder="+34 600 000 000"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  disabled={isSubmitting}
                  autoComplete="tel"
                />
              </div>
            ) : null}

            {error ? <p className="contact-lead-modal__error">{error}</p> : null}
          </form>

          <p className="contact-lead-modal__legal">
            Al enviar, aceptas que Trimergy te contacte con información sobre nuestros productos y servicios.
          </p>
        </>
      )}
    </BaseModal>
  );
}
